import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { Container, Row, Col, Accordion, Image, Modal } from "react-bootstrap";
import { motion } from "framer-motion";
import { Zap, TrendingUp, Users, Cloud, ShieldCheck, CheckCircle2, Car, Home, ShoppingBag, Briefcase, MessageSquare, Mic, Headphones, LineChart, Globe, Database, Cpu, Repeat, Landmark, GraduationCap } from "lucide-react";

import riyaHero from "../assets/images/banner/riya-ai-voice-agent.png";
import { RotateCcw } from "lucide-react";
import enquirySuccess from "../assets/images/enquiry-success.png";
import "../assets/css/contact.css";
import LayerFour from "../assets/images/layers/Layer_4.png";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import { apiUrl, apiHeaders } from "../apiConfig";

const DataReadinessModalContent = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    lastName: "",
    email: "",
    mobile: "",
    company: "",
    jobTitle: "",
    message: "",
    role: "",
    subscribe: "yes",
    heardAbout: "",
    otherChallenge: "",
  });

  const [loading, setLoading] = useState(false);
  const [responseMessage, setResponseMessage] = useState("");
  const [errors, setErrors] = useState({});

  const businessEmailRegex =
    /@(gmail\.com|yahoo\.com|outlook\.com|hotmail\.com|aol\.com|icloud\.com|protonmail\.com|zoho\.com|mail\.com|gmx\.com)$/i;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "First name is required";
    }

    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (businessEmailRegex.test(formData.email)) {
      newErrors.email = "Please use a business email address";
    }

    if (!formData.mobile) {
      newErrors.mobile = "Phone number is required";
    } else if (!/^\d{10}$/.test(formData.mobile)) {
      newErrors.mobile = "Please enter a valid 10-digit phone number";
    }

    if (!formData.company.trim()) {
      newErrors.company = "Company name is required";
    }

    if (!formData.jobTitle.trim()) {
      newErrors.jobTitle = "Job title is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResponseMessage("");
    setErrors({});

    if (!validateForm()) {
      setLoading(false);
      setResponseMessage("Please correct the errors in the form.");
      return;
    }

    try {
      // The server builds and sends the emails; SMTP credentials stay server-side
      const res = await fetch(apiUrl("/api/riya-demo"), {
        method: "POST",
        headers: apiHeaders,
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          mobile: formData.mobile,
          company: formData.company,
          jobTitle: formData.jobTitle,
        }),
      });
      const response = await res.json().catch(() => null);
      if (!response) {
        // Non-JSON reply: the API server is down or restarting
        setResponseMessage("We couldn’t reach our server. Please try again in a moment.");
      } else if (res.ok && response.success) {
        setIsSubmitted(true);
        setFormData((prev) => ({
          ...prev,
          name: "",
          email: "",
          mobile: "",
          company: "",
          jobTitle: "",
        }));
      } else {
        setResponseMessage(
          response.message || "Failed to send email. Please try again."
        );
      }
    } catch (error) {
      setResponseMessage("We couldn’t reach our server. Please try again in a moment.");
    }

    setLoading(false);
  };

  return (
    <>
      <div className="bg-E2F1FE position-relative box-shadow-one rounded-4 pt-5 p-23 p-sm-23">
        <Image
          src={LayerFour}
          className="position-absolute bottom-0 start-0 w-40"
        />

        <Row className="position-relative form-box z-0">
          {isSubmitted ? null : (
            <Col lg={5}>
              <div className="text-left d-flex justify-content-start">
                <h2 className="section-heading fw-700 fs-2">
                  Request
                  <span className="" style={{ color: "#028bc6" }}>
                    {" "}
                    a Personalized
                  </span>{" "}
                  <span className="bordered-text bordered-text-start">
                    Demo
                  </span>
                </h2>
              </div>
            </Col>
          )}
          <Col lg={isSubmitted ? 12 : 7}>
            {isSubmitted ? (
              <div
                className="enquiry-success enquiry-success--row"
                role="status"
                aria-live="polite"
              >
                <Image src={enquirySuccess} alt="" className="enquiry-success-img" />
                <div className="enquiry-success-body">
                  <h3 className="enquiry-success-title">Thank you</h3>
                  <p className="enquiry-success-subtitle">
                    We&rsquo;ve received your demo request.
                  </p>
                  <p className="enquiry-success-text">
                    Our team will get in touch with you soon to schedule your
                    personalized Riya AI demo. A confirmation has been sent to
                    your inbox.
                  </p>
                  <div className="enquiry-success-actions">
                    <button
                      type="button"
                      className="enquiry-success-link"
                      onClick={() => setIsSubmitted(false)}
                    >
                      <RotateCcw size={15} /> Submit another request
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <Form onSubmit={handleSubmit}>
                <Row className="pb-3 mt-3 mt-lg-0">
                  <Col lg={6}>
                    <Form.Group className="mb-1">
                      <Form.Label>
                        Name <span class="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder=""
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        isInvalid={!!errors.name}
                        className="rounded-1 custom-input custom-input-two"
                      />
                      {errors.name && (
                        <Form.Control.Feedback type="invalid">
                          {errors.name}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>
                  {/* <Col lg={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Last Name</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder=""
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        isInvalid={!!errors.lastName}
                        className="rounded-1 custom-input custom-input-two"
                      />
                      {errors.lastName && (
                        <Form.Control.Feedback type="invalid">
                          {errors.lastName}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col> */}
                  <Col lg={6}>
                    <Form.Group className="mb-1">
                      <Form.Label>
                        Company <span class="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        placeholder=""
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        isInvalid={!!errors.company}
                        className="rounded-1 custom-input custom-input-two"
                      />
                      {errors.company && (
                        <Form.Control.Feedback type="invalid">
                          {errors.company}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>

                  <Col lg={6}>
                    <Form.Group className="mb-1">
                      <Form.Label>
                        Email <span class="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="email"
                        placeholder=""
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        isInvalid={!!errors.email}
                        className="rounded-1 custom-input custom-input-two"
                      />
                      {errors.email && (
                        <Form.Control.Feedback type="invalid">
                          {errors.email}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>
                  <Col lg={6}>
                    <Form.Group className="mb-1">
                      <Form.Label>Job Title</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder=""
                        name="jobTitle"
                        value={formData.jobTitle}
                        onChange={handleChange}
                        isInvalid={!!errors.jobTitle}
                        className="rounded-1 custom-input custom-input-two"
                      />
                      {errors.jobTitle && (
                        <Form.Control.Feedback type="invalid">
                          {errors.jobTitle}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>
                  <Col lg={6}>
                    <Form.Group className="mb-1">
                      <Form.Label>Phone</Form.Label>
                      <Form.Control
                        type="tel"
                        name="mobile"
                        placeholder="Enter 10-digit phone number"
                        value={formData.mobile}
                        onChange={(e) => {
                          const value = e.target.value
                            .replace(/[^\d]/g, "")
                            .slice(0, 10);
                          setFormData({ ...formData, mobile: value });
                        }}
                        isInvalid={!!errors.mobile}
                        className="rounded-1 custom-input custom-input-two"
                      />
                      {errors.mobile && (
                        <Form.Control.Feedback type="invalid">
                          {errors.mobile}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>
                  {/* <Col lg={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>How did you hear about us?</Form.Label>
                      <Form.Select
                        name="heardAbout"
                        value={formData.heardAbout}
                        onChange={handleChange}
                        className="rounded-1 custom-input custom-input-two"
                        isInvalid={!!errors.heardAbout}
                      >
                        <option value="" disabled>
                          Select
                        </option>
                        <option value="Social Media">Social Media</option>
                        <option value="Friend">Friend</option>
                        <option value="Search Engine">Search Engine</option>
                        <option value="Advertisement">Advertisement</option>
                        <option value="Event">Event</option>
                        <option value="Other">Other</option>
                      </Form.Select>
                      {errors.heardAbout && (
                        <Form.Control.Feedback type="invalid">
                          {errors.heardAbout}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>
                  <Col lg={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>What is your role?</Form.Label>
                      <Form.Select
                        name="role"
                        value={formData.role || ""}
                        onChange={handleChange}
                        className="rounded-1 custom-input custom-input-two"
                        isInvalid={!!errors.role}
                      >
                        <option value="">Select</option>
                        <option value="Developer">Developer</option>
                        <option value="Manager">Manager</option>
                        <option value="Designer">Designer</option>
                        <option value="Marketer">Marketer</option>
                        <option value="Other">Other</option>
                      </Form.Select>
                      {errors.role && (
                        <Form.Control.Feedback type="invalid">
                          {errors.role}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>
                  <Col lg={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        How can we help your organization?
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        placeholder=""
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        isInvalid={!!errors.message}
                        className="rounded-1 custom-input custom-input-two"
                      />
                      {errors.message && (
                        <Form.Control.Feedback type="invalid">
                          {errors.message}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col> */}
                  {/* <Col lg={12}>
                    <Form.Group className="mb-3 mt-1">
                      <Form.Label className="mb-3">
                        I would like to receive emails from{" "}
                        <span className="fw-700">Comsense</span> with exclusive
                        updates on products, services, events, and alerts. I may
                        unsubscribe at any time.
                      </Form.Label>
                      <div>
                        <Form.Check
                          type="radio"
                          label="Yes"
                          name="subscribe"
                          value="yes"
                          className="d-block"
                          checked={formData.subscribe === "yes"}
                          onChange={handleChange}
                          inline
                        />
                        <Form.Check
                          type="radio"
                          label="No"
                          name="subscribe"
                          value="no"
                          className="d-block"
                          checked={formData.subscribe === "no"}
                          onChange={handleChange}
                          inline
                        />
                      </div>
                    </Form.Group>
                  </Col> */}

                  <Col lg={12} className="mt-4 text-start">
                    <>
                      <Button
                        variant=""
                        type="submit"
                        className="fw-600 d-block btn btn-danger btn-xl"
                        disabled={loading}
                      >
                        {loading ? "Submitting..." : "Submit"}
                      </Button>
                    </>
                  </Col>
                  {responseMessage && (
                    <p
                      className="mt-3 mb-0 fs-12 text-center"
                      style={{ color: loading ? "blue" : "red" }}
                    >
                      {responseMessage}
                    </p>
                  )}
                </Row>
              </Form>
            )}
          </Col>
        </Row>
      </div>
    </>
  );
};

const RiyaAiVoiceAgent = () => {
  const [showDataReadinessModal, setShowDataReadinessModal] = useState(false);

  const openDataReadinessModal = () => setShowDataReadinessModal(true);
  const closeDataReadinessModal = () => setShowDataReadinessModal(false);



  const useCases = [
    {
      id: "qualification",
      title: "Lead Qualification",
      icon: Users,
      angle: -90,
      details: [
        "Purchase intent collection",
        "Budget range assessment",
        "Buying timeline verification",
        "Product interest mapping",
        "Automatic CRM routing",
      ],
    },
    {
      id: "followups",
      title: "Intelligent Follow-Ups",
      icon: Repeat,
      angle: -38.5,
      details: [
        "Sales enquiry management",
        "Demo scheduling",
        "Lead nurturing calls",
        "Customer re-engagement",
        "Personalized conversation logs",
      ],
    },
    {
      id: "queries",
      title: "Query Resolution",
      icon: MessageSquare,
      angle: 13,
      details: [
        "Instant product answers",
        "Pricing information",
        "Availability checks",
        "Policy explanations",
        "Offer & promotion details",
      ],
    },
    {
      id: "complaints",
      title: "Complaint Management",
      icon: Headphones,
      angle: 64.5,
      details: [
        "Issue capture & categorization",
        "Smart team routing",
        "Complete interaction records",
        "Resolution tracking",
        "Feedback collection",
      ],
    },
    {
      id: "reminders",
      title: "Automated Reminders",
      icon: Zap,
      angle: 116,
      details: [
        "EMI & payment alerts",
        "Service appointments",
        "Subscription renewals",
        "Follow-up action triggers",
        "Timely voice notifications",
      ],
    },
  ];

  const [hoveredUseCase, setHoveredUseCase] = useState(useCases[0]);

  const industries = [
    {
      name: "Automotive",
      icon: Car,
      features: [
        "Lead qualification",
        "Test drive scheduling",
        "Service reminders",
      ],
    },
    {
      name: "Real Estate",
      icon: Home,
      features: [
        "Property enquiries",
        "Site visit scheduling",
        "Prospect qualification",
      ],
    },
    {
      name: "Retail & E-Commerce",
      icon: ShoppingBag,
      features: ["Customer support", "Promotional campaigns", "Order updates"],
    },
    {
      name: "BFSI",
      icon: Landmark,
      features: ["Loan screening", "Collections reminders", "Policy renewals"],
    },
    {
      name: "Healthcare",
      icon: ShieldCheck,
      features: [
        "Appointment reminders",
        "Patient follow-ups",
        "Outreach campaigns",
      ],
    },
    {
      name: "Education",
      icon: GraduationCap,
      features: [
        "Admissions enquiries",
        "Application updates",
        "Student engagement",
      ],
    },
  ];

  const metrics = [
    { label: "Lead Response Time", value: "Near Instant", icon: Zap },
    { label: "Agent Workload", value: "Reduced by up to 70%", icon: Briefcase },
    { label: "Call Capacity", value: "1,000+ simultaneous", icon: Cloud },
    { label: "Qualification Speed", value: "Significantly accelerated", icon: TrendingUp },
    { label: "Availability", value: "24/7", icon: Globe },
    { label: "Data Quality", value: "Automatically enriched", icon: Database },
  ];

  const outcomePositions = ["tl", "tr", "right", "br", "bl", "left"];
  const outcomeItems = metrics.map((metric, idx) => ({
    ...metric,
    pos: outcomePositions[idx] || "tl",
    code: `RIYA-OUT-${String(idx + 1).padStart(2, "0")}`,
  }));

  return (
    <div className="gcc-outfit">
      <Helmet>
        <title>
          Riya AI Voice Agent | Automate Customer Conversations | Comsense
        </title>
        <meta
          name="description"
          content="Riya AI Voice Agent is an enterprise-grade conversational AI platform that qualifies leads, answers queries, and automates follow-ups through natural voice."
        />
      </Helmet>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@100..900&display=swap');
        .gcc-outfit { font-family: 'Outfit', sans-serif; }
        
        .hero-section { background: #fff;  overflow: hidden; position: relative; }
        .hero-tag { display: inline-flex; align-items: center; gap: 8px; padding: 6px 16px; border-radius: 999px; background: #E2F1FE; color: #067bb1; border: 1px solid #b8daff; font-weight: 700; font-size: 12px; margin-bottom: 24px; }
        .hero-title { font-weight: 800; font-size: 42px; line-height: 1.1; letter-spacing:-0.02rem; color: #0f172a; margin-bottom: 24px; }
        .hero-subtitle { font-size: 1.1rem; line-height: 1.8; color: #475569; margin-bottom: 32px; max-width: 780px; }
        .highlight-red { color: #067bb1; }
        .hero-chip-row { display: flex; gap: 12px; flex-wrap: wrap; margin: 18px 0 28px; }
        .stat-card { display: flex; align-items: center; gap: 12px; }
        .stat-value { font-size: 16px; font-weight: 800; color: #0f172a; }
        .hero-media { border-radius: 16px; overflow: hidden; }
        
        .btn-red { background: #e00; color: #fff; border: none; border-radius: 12px; padding: 10px 18px; font-weight: 700; transition: all 0.3s ease; box-shadow: 0 10px 20px rgba(238, 0, 0, 0.2); }
        .btn-red:hover { transform: translateY(-2px); box-shadow: 0 15px 30px rgba(238, 0, 0, 0.3); color: #fff; }
        
        .section-title { font-size: 42px;
  font-weight: 800;
  color: #000;
  letter-spacing: -0.02em;
  margin-bottom: 16px; }
        .section-desc {font-size: 18px;
  color: #6b7280;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;}
        
        /* The Problem Section Styling */
        .problem-section { padding: 70px 0 70px 0; background: #fff; position: relative; overflow: hidden; }
        .problem-pill { display: inline-flex; align-items: center; gap: 8px; padding: 6px 16px; border-radius: 999px; background: #E2F1FE; color: #067bb1; border: 1px solid #b8daff; font-size: 12px; font-weight: 700; margin-bottom: 24px; }
        .problem-title { font-size: 42px; font-weight: 800; color: #000; letter-spacing: -0.02em; margin-bottom: 16px; }
        .problem-desc { font-size: 18px; color: #475569; max-width: auto; margin: 0 auto 60px; line-height: 1.6; }
        .problem-card { background: #fff; border: 2px solid #e5e7eb; border-radius: 24px; padding: 32px 24px; position: relative; overflow: hidden; z-index: 1; transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1); height: 100%; 
        box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1); 
        }

        .problem-card:hover { border-color: rgba(6,123,177,0.3); transform: translateY(-2px); box-shadow: 0 20px 40px -12px rgba(6,123,177,0.15); }
        .problem-icon-box { width: 64px; height: 64px; border-radius: 18px; display: flex; align-items: center; justify-content: center; margin-bottom: 24px; position: relative; z-index: 2; background: linear-gradient(180deg, #067bb1 0%, #04467a 100%); color: #fff; box-shadow: 0 4px 12px rgba(6,123,177,0.2); border: 1px solid rgba(6,123,177,0.25); transition: all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1); }
        .problem-icon-box svg { transition: transform 0.3s ease; }
        .problem-card:hover .problem-icon-box { transform: scale(1.1); box-shadow: 0 10px 25px rgba(6,123,177,0.4); }
        .problem-card:hover .problem-icon-box svg { transform: scale(1.1) rotate(0deg); }
        .problem-card-title { font-size: 20px; font-weight: 700; color: #0f172a; margin-bottom: 12px; transition: color 0.3s ease; }
        .problem-card:hover .problem-card-title { color: #067bb1; }
        .problem-card-text { font-size: 14px; color: #64748b; line-height: 1.5; margin-bottom: 0; }
        
        .shape-tr-circle { position: absolute; top: -60px; right: -60px; width: 150px; height: 150px; border-radius: 50%; background: transparent; border: 1px solid rgba(6,123,177,0.15); z-index: 0; pointer-events: none; }
        .shape-tr-inner { position: absolute; top: -30px; right: -30px; width: 120px; height: 120px; border-radius: 50%; background: radial-gradient(circle at center, rgba(6,123,177,0.04) 0%, transparent 70%); z-index: 0; }
        .shape-bl-circle { position: absolute; bottom: 0; left: 0; width: 100px; height: 100px; background: radial-gradient(circle at bottom left, rgba(6,123,177,0.1), transparent 70%); border-top-right-radius: 100%; opacity: 0.6; }
        .shape-diamond { position: absolute; top: 20px; right: 20px; width: 12px; height: 12px; background: #b8daff; transform: rotate(45deg); opacity: 0; transition: all 0.4s ease; }
        .problem-card:hover .shape-diamond { opacity: 1; transform: rotate(45deg) translate(10px, -10px); }
        .icon-glow { position: absolute; width: 100%; height: 100%; border-radius: 16px; filter: blur(12px); opacity: 0; transition: opacity 0.5s ease; z-index: -1; background: rgba(6,123,177,0.4); }
        .problem-card:hover .icon-glow { opacity: 0.6; }
        .mini-badge { position: absolute; bottom: -4px; right: -4px; width: 16px; height: 16px; background: #fff; border-radius: 50%; border: 2px solid #fff; box-shadow: 0 2px 4px rgba(0,0,0,0.1); transform: scale(0); transition: transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275); }
        .problem-card:hover .mini-badge { transform: scale(1); }
        .line-top { position: absolute; top: 0; left: 0; width: 100%; height: 3px; background: linear-gradient(90deg, transparent, #067bb1, transparent); opacity: 0; transition: opacity 0.5s ease; }
        .problem-card:hover .line-top { opacity: 1; }
        .line-bottom { position: absolute; bottom: 0; left: 0; width: 100%; height: 3px; background: linear-gradient(90deg, transparent, #067bb1, transparent); transform: scaleX(0); transform-origin: left; transition: transform 0.5s ease; }
        .problem-card:hover .line-bottom { transform: scaleX(1); }
        @keyframes rippleEffect { 0% { transform: scale(0.8); opacity: 0.5; } 100% { transform: scale(2.5); opacity: 0; } }
        .circle-border-anim { position: absolute; top: 10px; right: 10px; width: 40px; height: 40px; border: 1px solid rgba(6,123,177,0.3); border-radius: 50%; opacity: 0; pointer-events: none; }
        .problem-card:hover .circle-border-anim { animation: rippleEffect 0.8s ease-out; }
        
        /* Hexagonal Data Mesh Section Styling */
        .outcomes-section { 
          padding: 70px 0; 
          background: linear-gradient(180deg, #effdff 0%, #effdff 100%);
          position: relative; 
          overflow: hidden; 
        }

        .riya-outcomes-grid {
          display: grid;
          grid-template-columns: 1fr auto 1fr;
          grid-template-areas:
            "tl center tr"
            "left center right"
            "bl center br";
          gap: 22px;
          align-items: center;
          max-width: 1100px;
          margin: 0 auto;
          position: relative;
          z-index: 2;
        }

        .riya-outcomes-center { grid-area: center; display: flex; align-items: center; justify-content: center; }
        .riya-pos-tl { grid-area: tl; }
        .riya-pos-tr { grid-area: tr; }
        .riya-pos-left { grid-area: left; }
        .riya-pos-right { grid-area: right; }
        .riya-pos-bl { grid-area: bl; }
        .riya-pos-br { grid-area: br; }

        .riya-outcome-card {
          background: #fff;
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 18px;
          padding: 18px 18px;
          position: relative;
          box-shadow: 0 14px 34px rgba(15, 23, 42, 0.08);
          overflow: hidden;
          transition: transform 0.35s ease, box-shadow 0.35s ease, border-color 0.35s ease;
        }

        .riya-outcome-card::before {
          content: "";
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(6,123,177,0.08), transparent 55%);
          opacity: 0;
          transition: opacity 0.35s ease;
          pointer-events: none;
        }

        .riya-outcome-card:hover {
          transform: translateY(-6px);
          box-shadow: 0 22px 50px rgba(6, 123, 177, 0.14);
          border-color: rgba(6, 123, 177, 0.25);
        }

        .riya-outcome-card:hover::before { opacity: 1; }

        .riya-outcome-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 12px;
          position: relative;
          z-index: 1;
        }

        .riya-outcome-icon {
          width: 44px;
          height: 44px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(180deg, rgba(6, 123, 177, 0.12), rgba(6, 123, 177, 0.05));
          border: 1px solid rgba(6, 123, 177, 0.18);
          color: #04467a;
          flex-shrink: 0;
        }

        .riya-outcome-code {
          font-size: 11px;
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
          letter-spacing: 0.16em;
          color: rgba(2, 8, 23, 0.45);
          text-transform: uppercase;
          background: rgba(2, 8, 23, 0.04);
          border: 1px solid rgba(2, 8, 23, 0.06);
          padding: 6px 10px;
          border-radius: 999px;
          white-space: nowrap;
        }

        .riya-outcome-label {
          margin: 0 0 6px;
          font-size: 15px;
          font-weight: 800;
          color: #0f172a;
          letter-spacing: -0.01em;
          position: relative;
          z-index: 1;
        }

        .riya-outcome-value {
          margin: 0;
          font-size: 14px;
          line-height: 22px;
          color: #334155;
          position: relative;
          z-index: 1;
          font-weight: 600;
        }

        .riya-wheel {
          width: 380px;
          max-width: 80vw;
          aspect-ratio: 1 / 1;
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .riya-wheel-ring {
          width: 100%;
          height: 100%;
          border-radius: 50%;
          background:
            radial-gradient(circle at 30% 25%, rgba(255, 255, 255, 0.85) 0%, transparent 60%),
            linear-gradient(180deg, rgba(6, 123, 177, 0.55) 0%, rgba(4, 70, 122, 0.78) 100%);
          filter: drop-shadow(0 20px 60px rgba(6, 123, 177, 0.25));
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
        }



@media (max-width: 992px) {
  .hero-media {
    margin-top: 40px !important;
  }
}

        .riya-wheel-ring::after {
          content: "";
          position: absolute;
          inset: 18%;
          border-radius: 50%;
          background: #ffffff;
          box-shadow: 0 18px 45px rgba(15, 23, 42, 0.12);
          border: 10px solid rgba(226, 241, 254, 0.9);
        }

        .riya-wheel-hub {
          position: absolute;
          inset: 22%;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          z-index: 2;
          padding: 18px;
        }

        .riya-wheel-kicker {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          justify-content: center;
          font-weight: 800;
          color: #04467a;
          font-size: 12px;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          background: rgba(6, 123, 177, 0.08);
          border: 1px solid rgba(6, 123, 177, 0.18);
          padding: 6px 12px;
          border-radius: 999px;
          margin: 0 auto 10px;
          width: fit-content;
        }

        .riya-wheel-title {
          font-size: 18px;
          font-weight: 900;
          color: #0f172a;
          letter-spacing: -0.02em;
          line-height: 1.15;
        }

        .riya-wheel-subtitle {
          margin-top: 10px;
          font-size: 13px;
          line-height: 20px;
          color: #475569;
          font-weight: 600;
        }

        @media (max-width: 991px) {
          .riya-outcomes-grid {
            grid-template-columns: 1fr;
            grid-template-areas:
              "center"
              "tl"
              "tr"
              "right"
              "br"
              "bl"
              "left";
            gap: 16px;
          }
          .riya-wheel { width: 360px; margin: 0 auto 8px; }
        }
        
        
        .hex-mesh-container {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 20px;
          max-width: 1100px;
          margin: 0 auto;
          position: relative;
          z-index: 2;
        }
        
        @keyframes mesh-drift {
          0% { transform: translate(-50%, -50%) rotate(0deg); }
          100% { transform: translate(-50%, -50%) rotate(360deg); }
        }

        .hex-mesh-bg-glow {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 120%;
          height: 120%;
          background: 
            repeating-conic-gradient(from 0deg, rgba(6, 123, 177, 0.03) 0deg 30deg, transparent 30deg 60deg);
          z-index: -1;
          filter: blur(40px);
          animation: mesh-drift 60s linear infinite;
        }

        .hex-item {
          width: 280px;
          height: 320px;
          position: relative;
          clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
          background: linear-gradient(180deg, rgba(255, 255, 255, 0.95) 0%, rgba(226, 241, 254, 0.7) 100%);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(6, 123, 177, 0.2);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 40px 30px;
          transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
          cursor: pointer;
          box-shadow: 0 15px 35px rgba(6, 123, 177, 0.1), inset 0 0 15px rgba(255, 255, 255, 0.5);
        }

        .hex-item::before {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, #067bb1 0%, #04467a 100%);
          opacity: 0;
          transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
          z-index: 0;
          transform: scale(0.9);
          clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
        }

        .hex-item:hover {
          transform: scale(1.08) translateY(-20px);
          z-index: 10;
          background: transparent;
          border-color: transparent;
          box-shadow: 0 30px 60px -12px rgba(6, 123, 177, 0.4);
        }

        .hex-item:hover::before {
          opacity: 1;
          transform: scale(1);
        }

        /* Glow Effect on Hover */
        .hex-item::after {
          content: '';
          position: absolute;
          inset: -5px;
          background: radial-gradient(circle, #067bb1 0%, transparent 70%);
          opacity: 0;
          filter: blur(20px);
          z-index: -1;
          transition: all 0.5s ease;
        }

        .hex-item:hover::after {
          opacity: 0.6;
        }

        .hex-content {
          position: relative;
          z-index: 1;
          transition: all 0.5s ease;
        }

        .hex-item:hover .hex-content * {
          color: #fff !important;
        }

        .hex-item:hover .hex-val {
          transform: translateY(-5px);
          text-shadow: 0 10px 20px rgba(0,0,0,0.2);
        }

        .hex-val {
          font-size: 28px;
          font-weight: 800;
          color: #067bb1;
          margin-bottom: 8px;
          display: block;
          line-height: 1;
          transition: all 0.5s ease;
        }

        .hex-lab {
          font-size: 15px;
          font-weight: 700;
          color: #1e293b;
          line-height: 1.4;
          transition: all 0.5s ease;
        }

        .hex-item:hover .hex-lab {
          letter-spacing: 0.12em;
        }

        .hex-icon-bg {
          position: absolute;
          font-size: 140px;
          color: rgba(6, 123, 177, 0.04);
          top: -20px;
          right: -20px;
          transform: rotate(-15deg);
          z-index: -1;
          transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
          pointer-events: none;
          filter: grayscale(1);
        }

        .hex-item:hover .hex-icon-bg {
          color: rgba(255, 255, 255, 0.08);
          transform: rotate(5deg) scale(1.1) translate(-20px, 20px);
          filter: grayscale(0);
        }

        /* Technical accent */
        .hex-tech-label {
          position: absolute;
          bottom: 25px;
          left: 50%;
          transform: translateX(-50%);
          font-size: 10px;
          font-family: monospace;
          color: rgba(6, 123, 177, 0.3);
          letter-spacing: 0.2em;
          opacity: 0;
          transition: all 0.5s ease;
          pointer-events: none;
        }

        .hex-item:hover .hex-tech-label {
          opacity: 1;
          color: rgba(255, 255, 255, 0.5);
          bottom: 35px;
        }

        .mesh-line {
          position: absolute;
          background: rgba(6, 123, 177, 0.1);
          z-index: -1;
        }

        @media (max-width: 991px) {
          .hex-mesh-container { gap: 40px; }
          .hex-item { width: 240px; height: 280px; }
        }
        
        /* Circular Layout */
        .services-circular-container { position: relative; width: 100%; max-width: 1300px; height: 660px; margin: 12px auto 0; display: flex; align-items: center; justify-content: center; }
        .services-center-hub { width: 220px; height: 220px; background: #fff; border: 10px solid #E2F1FE; border-radius: 50%; display: flex; align-items: center; justify-content: center; text-align: center; z-index: 20; box-shadow: 0 15px 45px rgba(6, 123, 177, 0.15); }
        .service-satellite { position: absolute; width: 100px; height: 100px; z-index: 30; cursor: pointer; transition: all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1); }
        .service-satellite-icon-box { width: 100%; height: 100%; background: linear-gradient(180deg, #067bb1 0%, #04467a 100%); border-radius: 50%; display: flex; align-items: center; justify-content: center; color: #fff; border: 6px solid #fff; box-shadow: 0 10px 25px rgba(6, 123, 177, 0.2); }
        .service-satellite.active .service-satellite-icon-box { background: #e00; transform: scale(1.1); box-shadow: 0 0 30px rgba(238, 0, 0, 0.4); }
        .service-satellite-label { position: absolute; top: 105%; left: 50%; transform: translateX(-50%); width: 180px; text-align: center; font-size: 13px; font-weight: 700; color: #04467a; margin-top: 8px; background: rgba(255, 255, 255, 0.9); padding: 4px 6px; border-radius: 6px; backdrop-filter: blur(4px); border: 1px solid rgba(6,123,177,0.1); }
        .service-satellite.active .service-satellite-label { color: #e00; }
        
        .service-details-popover { 
          position: absolute; 
          right: 0; 
          top: 50%; 
          transform: translateY(-50%); 
          width: 300px; 
          background: rgba(255, 255, 255, 0.98); 
          backdrop-filter: blur(10px);
          border-radius: 24px; 
          padding: 25px; 
          box-shadow: 0 25px 60px rgba(6, 123, 177, 0.2); 
          z-index: 100;
          border: 1px solid rgba(6, 123, 177, 0.15); 
        }

        .service-hover-label-box { 
          position: absolute; 
          left: 0; 
          top: 50%; 
          transform: translateY(-50%); 
          width: 300px; 
          padding: 32px 32px 32px 0px; 
          text-align: left;
          z-index: 100; 
        }

        .service-hover-label-text { 
          font-family: 'Outfit', sans-serif;
          font-weight: 800; 
          font-size: 32px; 
          line-height: 1.1;
          color: #04467a; 
          letter-spacing: -0.02em;
        }
        
        .metric-card { background: #E2F1FE; border-radius: 20px; padding: 24px; text-align: center; height: 100%; border: 1px solid #b8daff; }
        .metric-value { font-size: 24px; font-weight: 800; color: #067bb1; margin-bottom: 8px; }
        .metric-label { font-size: 14px; color: #475569; font-weight: 600; }
        
        .faq-section { padding: 80px 0; background: #fff; }
        .faq-accordion.modern-faq-accordion .accordion-item {
          border: 1px solid rgba(6, 123, 177, 0.18);
          border-radius: 20px;
          background: #fff;
          box-shadow: 0 24px 60px rgba(15, 23, 42, 0.08);
          overflow: hidden;
          margin-bottom: 18px;
        }

        .faq-accordion.modern-faq-accordion .accordion-button {
          background: #f7fbff;
          color: #0f172a;
          font-size: 1.05rem;
          font-weight: 700;
          padding: 22px 28px;
          border: none;
          border-radius: 0;
          transition: background 0.3s ease, color 0.3s ease;
        }

        .faq-accordion.modern-faq-accordion .accordion-button:not(.collapsed) {
          background: #e7f2ff;
          color: #04467a;
        }

        .faq-accordion.modern-faq-accordion .accordion-button::after {
          width: 1.2rem;
          height: 1.2rem;
        }

        .faq-accordion.modern-faq-accordion .accordion-body {
          padding: 20px 28px 26px;
          color: #334155;
          font-size: 0.96rem;
          line-height: 1.8;
          background: #fff;
        }

        .faq-accordion.modern-faq-accordion .accordion-item.active .accordion-button {
          box-shadow: inset 0 -1px 0 rgba(0, 0, 0, 0.05);
        }

        .faq-heading { font-size: 42px; font-weight: 800; color: #000; letter-spacing: -0.02em; margin-bottom: 24px; }
        
        @media (min-width: 991px) and (max-width: 1199px) {
          .services-circular-container {
            height: 680px;
            margin-top: 10px;
            margin-bottom: 300px;
          }
          .service-hover-label-box {
            top: -10px !important;
            left: 50% !important;
            transform: translateX(-50%) !important;
            width: 100% !important;
            text-align: center !important;
            padding: 0 !important;
          }
          .service-hover-label-text {
            font-size: 30px !important;
            text-align: center !important;
          }
          .service-details-popover {
            top: calc(100% + 10px) !important;
            left: 50% !important;
            transform: translateX(-50%) !important;
            width: 95% !important;
            max-width: 550px !important;
            position: absolute !important;
          }
        }

        /* GCC Styles Ported for Built for High-Volume Engagement */
        .proven-outcomes-section {
          padding: 70px 0 80px 0;
          background-color: #e2f1fe;
          position: relative;
          overflow: hidden;
        }
        
        .outcome-title {
          color: #000;
          font-size: 42px;
          font-weight: 800;
          margin-bottom: 24px;
          letter-spacing: -0.02em;
          line-height: 1.1;
        }

        .outcome-orb-left {
          position: absolute;
          width: 500px;
          height: 500px;
          background: radial-gradient(circle, rgba(6, 123, 177, 0.05) 0%, transparent 70%);
          border-radius: 50%;
          filter: blur(150px);
          top: 40%;
          left: -100px;
          z-index: 0;
          animation: pulseGlow 4s ease-in-out infinite;
        }
        
        .outcome-orb-right {
          position: absolute;
          width: 384px;
          height: 384px;
          background: radial-gradient(circle, rgba(6, 123, 177, 0.03) 0%, transparent 70%);
          border-radius: 50%;
          filter: blur(120px);
          top: 10%;
          right: -50px;
          z-index: 0;
          animation: pulseGlow 5s ease-in-out infinite 1.5s;
        }

        .riya-engagement-scroll {
          overflow-x: auto;
          padding: 18px 4px 4px;
          -webkit-overflow-scrolling: touch;
        }

        .riya-engagement-scroll::-webkit-scrollbar {
          height: 10px;
        }

        .riya-engagement-scroll::-webkit-scrollbar-track {
          background: rgba(15, 23, 42, 0.06);
          border-radius: 999px;
        }

        .riya-engagement-scroll::-webkit-scrollbar-thumb {
          background: rgba(6, 123, 177, 0.28);
          border-radius: 999px;
        }

        .riya-engagement-track {
          position: relative;
          display: flex;
          justify-content: space-between;
          gap: 26px;
          min-width: 1040px;
          max-width: 1200px;
          margin: 0 auto;
          padding: 10px 6px;
          height: 380px;
        }

        .riya-engagement-track::before {
          content: "";
          position: absolute;
          left: 0;
          right: 0;
          top: 50%;
          height: 2px;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(15, 23, 42, 0.18),
            transparent
          );
          z-index: 0;
        }

        .riya-engagement-item {
          position: relative;
          flex: 0 0 180px;
          height: 100%;
          z-index: 1;
          --connector-len: 52px;
        }

        .riya-engagement-label {
          position: absolute;
          left: 50%;
          transform: translateX(-50%);
          width: 220px;
          max-width: 260px;
          background: rgba(255, 255, 255, 0.92);
          backdrop-filter: blur(8px);
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 16px;
          padding: 14px 14px;
          min-height: 96px;
          box-shadow: 0 14px 34px rgba(15, 23, 42, 0.08);
          transition: transform 0.35s ease, box-shadow 0.35s ease, border-color 0.35s ease;
        }

        .riya-engagement-item.is-top .riya-engagement-label { top: 0; }
        .riya-engagement-item.is-bottom .riya-engagement-label { bottom: 0; }

        .riya-engagement-label::before,
        .riya-engagement-label::after {
          content: "";
          position: absolute;
          left: 50%;
          transform: translateX(-50%);
          pointer-events: none;
        }

        .riya-engagement-item.is-top .riya-engagement-label::before {
          width: 12px;
          height: 12px;
          border-radius: 999px;
          background: linear-gradient(180deg, #067bb1 0%, #04467a 100%);
          top: calc(100% - 6px);
          box-shadow: 0 10px 24px rgba(15, 23, 42, 0.18);
        }

        .page-section
        {
        padding: 70px 0 70px 0;
        }

        .riya-engagement-item.is-top .riya-engagement-label::after {
          width: 3px;
          height: var(--connector-len);
          border-radius: 999px;
          background: linear-gradient(180deg, #067bb1 0%, #04467a 100%);
          top: calc(100% + 6px);
        }

        .riya-engagement-item.is-bottom .riya-engagement-label::before {
          width: 12px;
          height: 12px;
          border-radius: 999px;
          background: linear-gradient(180deg, #067bb1 0%, #04467a 100%);
          bottom: calc(100% - 6px);
          box-shadow: 0 10px 24px rgba(15, 23, 42, 0.18);
        }

        .riya-engagement-item.is-bottom .riya-engagement-label::after {
          width: 3px;
          height: var(--connector-len);
          border-radius: 999px;
          background: linear-gradient(180deg, #04467a 0%, #067bb1 100%);
          bottom: calc(100% + 6px);
        }

        .riya-engagement-item:hover .riya-engagement-label {
          transform: translateX(-50%) translateY(-4px);
          box-shadow: 0 22px 52px rgba(6, 123, 177, 0.14);
          border-color: rgba(6, 123, 177, 0.18);
        }

        .riya-engagement-title {
          display: flex;
          align-items: center;
          gap: 10px;
          font-weight: 900;
          color: #0f172a;
          font-size: 14px;
          letter-spacing: -0.01em;
          margin-bottom: 6px;
        }

        .riya-engagement-icon {
          width: 38px;
          height: 38px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(180deg, #067bb1 0%, #04467a 100%);
          border: 1px solid rgba(6, 123, 177, 0.18);
          color: #fff;
          flex-shrink: 0;
        }

        .riya-engagement-sub {
          color: #475569;
          font-size: 12px;
          line-height: 18px;
          font-weight: 600;
        }

        .riya-engagement-node {
          position: absolute;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
          width: 94px;
          height: 94px;
        }

        .riya-engagement-ring {
          width: 100%;
          height: 100%;
          border-radius: 999px;
          background:
            radial-gradient(circle at 30% 25%, rgba(255, 255, 255, 0.85) 0%, transparent 60%),
            linear-gradient(180deg, rgba(6, 123, 177, 0.55) 0%, rgba(4, 70, 122, 0.78) 100%);
        background: linear-gradient(180deg, #067bb1 0%, #04467a 100%);
            padding: 10px;
          box-shadow: 0 18px 55px rgba(15, 23, 42, 0.14);
          transition: transform 0.45s ease, box-shadow 0.45s ease, filter 0.45s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .riya-engagement-inner {
          width: 100%;
          height: 100%;
          border-radius: 999px;
          background: #ffffff;
          border: 10px solid rgba(255, 255, 255, 0.85);
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: inset 0 0 0 1px rgba(15, 23, 42, 0.06);
        }

        .riya-engagement-step {
          color: #04467a;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .riya-engagement-stem {
          display: none;
        }

        .riya-engagement-dot {
          display: none;
        }

        .riya-engagement-item:hover .riya-engagement-ring {
          transform: rotate(8deg) scale(1.02);
          filter: saturate(1.05);
          box-shadow: 0 26px 70px rgba(6, 123, 177, 0.22);
        }

        @media (max-width: 991px) {
          .riya-engagement-track {
            min-width: 980px;
            height: 360px;
          }
          .riya-engagement-item { flex-basis: 170px; }
          .riya-engagement-label { width: 210px; }
        }

        .outcome-card {
          background: #fff;
          border-radius: 24px;
          padding: 40px;
          height: 100%;
          position: relative;
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
          border: 2px solid #e6f0ff;
          transition: all 0.5s ease;
          z-index: 1;
          overflow: hidden;
        }

        .outcome-card:hover {
          border-color: rgba(6, 123, 177, 0.5);
          box-shadow: 0 25px 50px -12px rgba(6, 123, 177, 0.15);
          transform: translateY(-5px);
        }

        .outcome-icon-box {
          width: 64px;
          height: 64px;
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          transition: transform 0.5s ease;
          box-shadow: 0 10px 15px -3px rgba(6,123,177,0.3);
          flex-shrink: 0;
          background: linear-gradient(180deg, #067bb1 0%, #04467a 100%);
          color: #fff;
        }

        @keyframes pulseGlow {
          0%, 100% { opacity: 0.5; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.1); }
        }

        /* Enterprise-Ready Platform Section - Advanced Styling */
        .platform-section { padding: 80px 0; background: #fff; position: relative; overflow: hidden; }
        .platform-pill { display: inline-flex; align-items: center; gap: 8px; padding: 6px 16px; border-radius: 999px; background: #E2F1FE; color: #067bb1; border: 1px solid #b8daff; font-size: 12px; font-weight: 700; margin-bottom: 24px; }
        
        .platform-card { 
          position: relative; 
          background: #fff; 
          border-radius: 24px; 
          padding: 40px 32px; 
          border: 2px solid #e2e8f0; 
          height: 100%; 
          overflow: hidden; 
          box-shadow: rgba(0, 0, 0, 0.1) 0px 10px 15px -3px;
          transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
          z-index: 1;
        }
        
        .platform-card:hover { 
          border-color: rgba(6,123,177,0.4);
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.15);
          transform: translateY(-5px);
        }
        
        .platform-icon-box { 
          width: 64px; 
          height: 64px; 
          border-radius: 16px; 
          background: linear-gradient(180deg, #067bb1 0%, #04467a 100%); 
          display: flex; 
          align-items: center; 
          justify-content: center; 
          margin-bottom: 24px; 
          color: white; 
          position: relative; 
          z-index: 2; 
          transition: all 0.5s ease;
          box-shadow: 0 10px 20px rgba(6, 123, 177, 0.2);
        }

        .platform-card:hover .platform-icon-box {
          transform: scale(1.1) rotate(6deg);
        }

        .platform-card-title { 
          font-size: 24px; 
          font-weight: 800; 
          color: #0f172a; 
          margin-bottom: 16px; 
          transition: color 0.3s ease; 
        }

        .platform-card:hover .platform-card-title {
          color: #067bb1;
        }

        .platform-list-item { 
          display: flex; 
          align-items: center; 
          gap: 12px; 
          margin-bottom: 12px; 
          font-size: 15px; 
          color: #475569; 
          font-weight: 500;
          transition: all 0.3s ease;
        }

        .platform-check-icon { 
          width: 24px; 
          height: 24px; 
          background: #E2F1FE; 
          border-radius: 8px; 
          display: flex; 
          align-items: center; 
          justify-content: center; 
          color: #067bb1; 
          flex-shrink: 0;
        }

        .platform-shape-tr { 
          position: absolute; 
          top: 0; 
          right: 0; 
          width: 120px; 
          height: 120px; 
          background: radial-gradient(circle at top right, rgba(6,123,177,0.1), transparent 70%); 
          border-bottom-left-radius: 100%; 
          z-index: 0;
          opacity: 0.6;
        }

        .launch-container {
          position: relative;
          background: linear-gradient(180deg, #effdff 0%, #effdff 100%);
          border-radius: 24px;
          border: 1px solid rgba(56, 164, 229, 0.5);
          box-shadow: 0 0 0 1px rgba(24, 170, 227, 0.2), 0 20px 50px -12px rgba(24, 132, 227, 0.25);
          overflow: hidden;
        }
        
        .launch-grid-bg {
          position: absolute;
          inset: 0;
          background-image: linear-gradient(rgba(6, 123, 177, 0.03) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(6, 123, 177, 0.03) 1px, transparent 1px);
          background-size: 24px 24px;
          opacity: 0.3;
          z-index: 0;
          pointer-events: none;
        }
        
        .launch-top-accent {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 1px;
          background: linear-gradient(90deg, transparent, rgb(221, 244, 255), transparent);
          box-shadow: 0 1px 10px rgb(239, 253, 255);
          z-index: 2;
        }
        
        .launch-corner-tr {
          position: absolute;
          top: 20px;
          right: 20px;
          width: 40px;
          height: 40px;
          border-top: 2px solid rgb(221, 244, 255);
          border-right: 2px solid rgb(221, 244, 255);
          border-top-right-radius: 8px;
          z-index: 2;
        }
        
        .launch-corner-bl {
          position: absolute;
          bottom: 20px;
          left: 20px;
          width: 40px;
          height: 40px;
          border-bottom: 2px solid rgb(221, 244, 255);
          border-left: 2px solid rgb(221, 244, 255);
          border-bottom-left-radius: 8px;
          z-index: 2;
        }
        
        .launch-content {
          position: relative;
          z-index: 1;
        }

        .launch-title {
          font-size: 42px;
          font-weight: 800;
          color: #000;
          margin-bottom: 16px;
          letter-spacing: -0.02em;
        }
        
        .launch-subtitle {
          font-size: 18px;
          max-width: 600px;
          margin: 0 auto;
        }

        @media (max-width: 991px) {
          .services-circular-container { display: none; }
          .hero-title { font-size: 36px; }
          .launch-title { font-size: 32px; }
        }
      `}</style>

      {/* Hero Section */}
      <section className="hero-section pt-5 pb-5 masthead z-0">
        <Container className="position-relative">
          <Row className="align-items-center justify-content-between">
            <Col lg={7}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                className="pe-3"
              >
                <h1 className="hero-title text-black mb-3">
                  Automate Customer Conversations.{" "}
                  <span className="highlight-red">Qualify Leads Faster.</span>
                </h1>
                <p className="hero-subtitle text-black ls-minus-045 fw-500 fs-5 mb-4 opacity-75">
                  Riya AI Voice Agent is an enterprise-grade conversational AI
                  platform that engages prospects, qualifies leads, and answers
                  customer queries through natural, human-like voice
                  conversations.
                </p>

                <div className="hero-chip-row mb-4">
                  <div className="stat-card">
                    <div className="stat-value">• Automate Conversations</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-value">• Qualify Leads Faster</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-value">
                      • Accelerate Revenue Growth
                    </div>
                  </div>
                </div>

                <div className="d-flex flex-wrap gap-3 mt-4">
                  <button
                    type="button"
                    className="btn-red whitespace-nowrap"
                    onClick={openDataReadinessModal}
                  >
                    Request a Demo
                  </button>
                </div>
              </motion.div>
            </Col>
            <Col lg={5} className="text-center">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6 }}
                className="hero-media m-auto"
                style={{ width: "90%" }}
              >
                <Image src={riyaHero} alt="Riya AI hero" className="w-100" />
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Problem Section */}
      <section className="problem-section page-section" style={{ background: 'rgb(226, 241, 254)' }}>
        <Container>
          <div className="text-center mb-5">
            {/* <div className="problem-pill mx-auto">
              <Zap size={14} /> The Efficiency Gap
            </div> */}
            <h2 className="problem-title">The Cost of Missed Conversations</h2>
            <p className="problem-desc">
              Customer demand grows faster than teams can respond. Don't let
              your revenue slip through the cracks.
            </p>
          </div>
          <Row className="g-4">
            {[
              {
                title: "Delayed Response",
                desc: "Leads go cold when not addressed immediately.",
                icon: Mic,
              },
              {
                title: "Limited Capacity",
                desc: "Teams can only handle a finite number of calls.",
                icon: Headphones,
              },
              {
                title: "Rising Costs",
                desc: "Operational expenses grow with every new hire.",
                icon: Zap,
              },
              {
                title: "Inconsistent Process",
                desc: "Manual follow-ups are prone to human error.",
                icon: MessageSquare,
              },
            ].map((item, idx) => (
              <Col md={3} key={idx}>
                <motion.div
                  className="problem-card"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                >
                  <div className="line-top"></div>
                  <div className="line-bottom"></div>
                  <div className="shape-tr-circle"></div>
                  <div className="shape-tr-inner"></div>
                  <div className="shape-bl-circle"></div>
                  <div className="shape-diamond"></div>
                  <div className="circle-border-anim"></div>

                  <div className="problem-icon-box">
                    <div className="icon-glow"></div>
                    <item.icon size={28} />
                    <div className="mini-badge"></div>
                  </div>

                  <h3 className="problem-card-title">{item.title}</h3>
                  <p className="problem-card-text">{item.desc}</p>
                </motion.div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Use Cases Section - Circular */}
      <section className="page-section overflow-hidden pb-4">
        <Container>
          <div className="text-center mb-1">
            <h2 className="section-title">One AI Agent. Multiple Use Cases.</h2>
          </div>

          <div className="services-circular-container">
            <div className="services-center-hub">
              <div className="fw-800 text-04467a fs-4">RIYA AI</div>
            </div>

            <div className="service-hover-label-box">
              {hoveredUseCase && (
                <motion.div
                  key={hoveredUseCase.id}
                  initial={{ opacity: 0, x: -20, y: "-50%" }}
                  animate={{ opacity: 1, x: 0, y: "-50%" }}
                  className="service-hover-label-text"
                  style={{ position: "absolute", top: "50%", width: "100%" }}
                >
                  {hoveredUseCase.title}
                </motion.div>
              )}
            </div>

            {useCases.map((service, idx) => {
              const total = useCases.length || 1;
              const angleDeg = -90 + idx * (360 / total);
              const angleRad = (angleDeg * Math.PI) / 180;
              const radius = 220;
              const x = Math.cos(angleRad) * radius;
              const y = Math.sin(angleRad) * radius;
              return (
                <React.Fragment key={service.id}>
                  <div
                    className="service-connection-line"
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      height: "1px",
                      width: `${radius}px`,
                      transform: `rotate(${angleDeg}deg)`,
                      transformOrigin: "left center",
                      backgroundImage:
                        "linear-gradient(to right, rgba(6, 123, 177, 0.3) 50%, transparent 50%)",
                      backgroundSize: "8px 1px",
                      backgroundRepeat: "repeat-x",
                    }}
                  ></div>
                  <motion.div
                    className={`service-satellite ${hoveredUseCase?.id === service.id ? "active" : ""}`}
                    style={{
                      left: `calc(50% + ${x}px - 50px)`,
                      top: `calc(50% + ${y}px - 50px)`,
                    }}
                    onMouseEnter={() => setHoveredUseCase(service)}
                    whileHover={{ scale: 1.1 }}
                  >
                    <div className="service-satellite-icon-box">
                      <service.icon size={36} />
                    </div>
                    <div className="service-satellite-label">
                      {service.title}
                    </div>
                  </motion.div>
                </React.Fragment>
              );
            })}

            {hoveredUseCase && (
              <motion.div
                key={hoveredUseCase.id + "pop"}
                initial={{ opacity: 0, x: 20, y: "-50%" }}
                animate={{ opacity: 1, x: 0, y: "-50%" }}
                className="service-details-popover"
              >
                <div className="fw-800 text-04467a fs-5 mb-3 d-flex align-items-center gap-2">
                  <hoveredUseCase.icon size={20} /> {hoveredUseCase.title}
                </div>
                <ul className="list-unstyled mb-0">
                  {hoveredUseCase.details.map((detail, idx) => (
                    <li
                      key={idx}
                      className="small mb-2 d-flex align-items-start gap-2"
                    >
                      <CheckCircle2
                        size={16}
                        className="text-067bb1 flex-shrink-0"
                        style={{ color: "#067bb1" }}
                      />{" "}
                      {detail}
                    </li>
                  ))}
                </ul>
              </motion.div>
            )}
          </div>

          {/* Mobile Grid */}
          <div className="d-lg-none mt-4">
            <Row className="g-4">
              {useCases.map((item) => (
                <Col md={6} key={item.id}>
                  <div className="problem-card">
                    <div className="d-flex align-items-center gap-3 mb-3">
                      <div className="bg-E2F1FE p-2 rounded-3 text-067bb1">
                        <item.icon size={24} />
                      </div>
                      <h4 className="fw-800 fs-5 mb-0">{item.title}</h4>
                    </div>
                    <ul className="list-unstyled mb-0 small">
                      {item.details.map((d, i) => (
                        <li key={i} className="mb-1">
                          ✓ {d}
                        </li>
                      ))}
                    </ul>
                  </div>
                </Col>
              ))}
            </Row>
          </div>
        </Container>
      </section>

      {/* Metrics Section - Hexagonal Mesh Design */}
      <section className="outcomes-section">
        <Container>
          <div className="text-center mb-5">
            <h2 className="section-title">Expected Outcomes with Riya</h2>
            <p className="section-desc max-w-700 mx-auto">
              Strategic impact delivered through advanced conversational intelligence.
            </p>
          </div>

          <div className="riya-outcomes-grid">
            <motion.div
              className="riya-outcomes-center"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="riya-wheel">
                <div className="riya-wheel-ring" aria-hidden="true"></div>
                <div className="riya-wheel-hub">
                  <div>
                    <div className="riya-wheel-kicker">
                      <Mic size={14} /> RIYA
                    </div>
                    <div className="riya-wheel-title">Expected Outcomes</div>
                    <div className="riya-wheel-subtitle">
                      Measurable business impact from voice automation.
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {outcomeItems.map((item, idx) => (
              <motion.div
                key={item.code}
                className={`riya-outcome-card riya-pos-${item.pos}`}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.55, delay: idx * 0.05 }}
                whileHover={{ y: -6 }}
              >
                <div className="riya-outcome-top">
                  <div className="riya-outcome-icon">
                    <item.icon size={20} />
                  </div>
                  {/* <div className="riya-outcome-code">{item.code}</div> */}
                </div>
                <h3 className="riya-outcome-label">{item.label}</h3>
                <p className="riya-outcome-value">{item.value}</p>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      {/* Enterprise-Ready Platform Section */}
      <section className="platform-section">
        <Container>
          <div className="text-center mb-5">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              {/* <div className="platform-pill mx-auto">
                <Settings size={14} /> Robust Architecture
              </div> */}
              <h2 className="section-title">Enterprise-Ready Platform</h2>
              <p className="section-desc mx-auto">
                Riya combines advanced AI technologies with seamless business
                integrations to provide a robust, scalable solution.
              </p>
            </motion.div>
          </div>

          <Row className="g-4">
            {[
              {
                icon: Cpu,
                title: "AI & Intelligence",
                list: [
                  "Large Language Models",
                  "Natural Language Processing",
                  "Sentiment Analysis",
                ],
              },
              {
                icon: Database,
                title: "Integrations",
                list: [
                  "CRM Synchronization",
                  "Lead Management Systems",
                  "WhatsApp & SMS",
                ],
              },
              {
                icon: ShieldCheck,
                title: "Enterprise Features",
                list: [
                  "Multilingual Support",
                  "24/7 Availability",
                  "Secure Data Handling",
                ],
              },
              {
                icon: LineChart,
                title: "Business Analytics",
                list: [
                  "Real-Time Analytics",
                  "Intelligent Scoring",
                  "Performance Insights",
                ],
              },
            ].map((item, idx) => (
              <Col lg={3} md={6} key={idx}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="h-100"
                >
                  <div className="platform-card group h-100">
                    <div className="platform-shape-tr"></div>
                    <div className="platform-icon-box">
                      <item.icon size={32} />
                    </div>
                    <h3 className="platform-card-title">{item.title}</h3>
                    <ul className="list-unstyled mb-0">
                      {item.list.map((l, i) => (
                        <li key={i} className="platform-list-item">
                          <div className="platform-check-icon">
                            <CheckCircle2 size={14} />
                          </div>
                          {l}
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Built for High-Volume Engagement - GCC Design Ported */}
      <section className="proven-outcomes-section">
        <div className="outcome-orb-left"></div>
        <div className="outcome-orb-right"></div>
        <Container className="position-relative z-1">
          <div className="text-center mb-5">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="outcome-title">Built for High-Volume Engagement</h2>
              <p className="section-desc max-w-700 mx-auto">
                Riya provides specialized voice automation across key industry sectors.
              </p>
            </motion.div>
          </div>

          <div className="riya-engagement-scroll">
            <div className="riya-engagement-track">
              {industries.map((ind, index) => {
                const positionClass = index % 2 === 0 ? "is-top" : "is-bottom";
                const featuresLine = ind.features.join(" • ");

                return (
                  <motion.div
                    key={ind.name}
                    className={`riya-engagement-item ${positionClass}`}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.55, delay: index * 0.06 }}
                    whileHover={{ y: -4 }}
                  >
                    <div className="riya-engagement-label">
                      <div className="riya-engagement-title">
                        {/* <div className="riya-engagement-icon">
                          <ind.icon size={18} />
                        </div> */}
                        {ind.name}
                      </div>
                      <div className="riya-engagement-sub">{featuresLine}</div>
                    </div>

                    <div className="riya-engagement-node" aria-hidden="true">
                      <div className="riya-engagement-ring">
                        <div className="riya-engagement-inner">
                          <span className="riya-engagement-step">
                            <ind.icon size={22} />
                          </span>
                        </div>
                      </div>
                      <span className="riya-engagement-stem" />
                      <span className="riya-engagement-dot" />
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </Container>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <Container>
          <div className="text-center mb-5">
            <h2 className="faq-heading">Frequently Asked Questions</h2>
          </div>
          <Row className="justify-content-center mb-5">
            <Col lg={10}>
              <Accordion
                defaultActiveKey="0"
                className="faq-accordion modern-faq-accordion"
              >
                {[
                  {
                    q: "What is an AI Voice Agent?",
                    a: "An AI Voice Agent is an intelligent conversational system that can engage customers through natural voice interactions, understand intent, answer questions, qualify leads, and automate follow-up activities without human intervention.",
                  },
                  {
                    q: "How is Riya different from a traditional IVR system?",
                    a: "Traditional IVR systems rely on predefined menus and scripted responses. Riya uses conversational AI to understand context, respond dynamically, and conduct natural two-way conversations that adapt to customer responses in real time.",
                  },
                  {
                    q: "Can Riya qualify leads automatically?",
                    a: "Yes. Riya can engage prospects, identify purchase intent, collect qualification details such as budget and timeline, and prioritize high-potential opportunities for sales teams.",
                  },
                  {
                    q: "Which industries can benefit from Riya AI Voice Agent?",
                    a: "Riya can support organizations across automotive, BFSI, real estate, healthcare, education, retail, e-commerce, and other industries that manage high volumes of customer interactions.",
                  },
                  {
                    q: "Can Riya integrate with our CRM?",
                    a: "Riya can integrate with CRM and lead management platforms to automatically update customer records, capture conversation outcomes, and support workflow automation.",
                  },
                  {
                    q: "Can Riya handle customer support as well as sales conversations?",
                    a: "Yes. Riya can support lead qualification, follow-ups, reminders, customer enquiries, complaint logging, appointment scheduling, and other customer engagement workflows.",
                  },
                  {
                    q: "How quickly can Riya be deployed?",
                    a: "Deployment timelines vary depending on integration and workflow requirements. Most implementations begin with a discovery phase to define use cases, integrations, and conversation flows before rollout.",
                  },
                ].map((faq, idx) => (
                  <Accordion.Item
                    eventKey={idx.toString()}
                    key={idx}
                    className="faq-card-item"
                  >
                    <Accordion.Header>{faq.q}</Accordion.Header>
                    <Accordion.Body>{faq.a}</Accordion.Body>
                  </Accordion.Item>
                ))}
              </Accordion>

              <div className="mt-5 pt-4">
                <div className="text-center mb-5">
                  <h3 className="faq-heading fs-3">
                    Frequently Asked by Business Leaders
                  </h3>
                </div>
                <Accordion className="faq-accordion modern-faq-accordion">
                  {[
                    {
                      q: "How Can AI Voice Agents Improve Lead Conversion Rates?",
                      a: "Many organizations lose potential revenue because leads are not contacted quickly enough or follow-up activities are inconsistent. AI voice agents help address these challenges by engaging prospects immediately, maintaining timely follow-ups, and identifying high-intent opportunities. This enables sales teams to focus on qualified prospects and accelerate conversion cycles.",
                    },
                    {
                      q: "Can AI Voice Agents Replace Human Calling Teams?",
                      a: "AI voice agents are most effective when they augment rather than replace human teams. They automate repetitive and high-volume interactions such as lead qualification, reminders, follow-ups, and routine enquiries, allowing employees to focus on complex conversations, relationship building, and revenue-generating activities.",
                    },
                    {
                      q: "How Can Businesses Scale Customer Engagement Without Increasing Headcount?",
                      a: "Traditional customer outreach models often require proportional increases in staffing as interaction volumes grow. AI voice agents enable organizations to manage significantly higher conversation volumes while maintaining consistent customer experiences, reducing operational strain and improving efficiency.",
                    },
                    {
                      q: "How Can AI Help Sales Teams Focus on High-Intent Prospects?",
                      a: "AI-powered conversations capture customer intent, buying signals, timelines, and engagement patterns in real time. By automatically prioritizing opportunities based on qualification criteria, sales teams can spend more time engaging prospects who are most likely to convert.",
                    },
                    {
                      q: "How Can Organizations Measure the ROI of AI Voice Automation?",
                      a: "Common success metrics include improved lead conversion rates, reduced response times, lower operational costs, increased engagement volumes, higher agent productivity, improved customer satisfaction, and more accurate customer intelligence. The specific ROI depends on business objectives, use cases, and deployment scale.",
                    },
                  ].map((faq, idx) => (
                    <Accordion.Item
                      eventKey={(idx + 10).toString()}
                      key={idx}
                      className="faq-card-item"
                    >
                      <Accordion.Header>{faq.q}</Accordion.Header>
                      <Accordion.Body>{faq.a}</Accordion.Body>
                    </Accordion.Item>
                  ))}
                </Accordion>
              </div>
            </Col>
          </Row>

          {/* Transform Conversations into Advantage - Final CTA Design Ported */}
          <motion.div
            className="launch-container text-center mt-5"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="launch-top-accent"></div>
            <div className="launch-corner-tr"></div>
            <div className="launch-corner-bl"></div>
            <div className="launch-grid-bg"></div>
            <div className="launch-content py-5">
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="launch-title mb-3"
              >
                Transform Conversations into Advantage
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="launch-subtitle mb-4 text-dark"
              >
                Ready to see how Riya AI can revolutionize your customer
                engagement? Book a demo today and experience the future of voice
                automation.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
              >
                <button
                  type="button"
                  className="btn-red text-decoration-none d-inline-block"
                  onClick={openDataReadinessModal}

                >
                  Book a Free Demo
                </button>
              </motion.div>
            </div>
          </motion.div>
        </Container>
      </section>

      <Modal
        show={showDataReadinessModal}
        size="xl"
        onHide={closeDataReadinessModal}
        aria-labelledby="contained-modal-title-vcenter"
        centered
        style={{ overflow: "hidden" }}
      >
        <style>
          {`.btn-close {
                    position: absolute;
                    top: 1rem;
                    right: 1rem;
                    z-index: 2;
                  }
                  .modal-content{overflow:hidden !important;}
                  `}
        </style>
        <Modal.Header className="p-0" closeButton></Modal.Header>
        <Modal.Body className="p-0">
          <DataReadinessModalContent
            key={showDataReadinessModal ? "open" : "closed"}
          />
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default RiyaAiVoiceAgent;
